import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TERMS_VERSION, brandByKey, legalDateLabel } from "@school-sales-ai/shared";
import { Wordmark } from "@/components/Wordmark";
import { TermsContent } from "@/components/legal/TermsContent";
import { useAcceptTerms } from "@/hooks/useAcceptTerms";
import { useClientBranding } from "@/hooks/useClientBranding";
import { brandFromLocation, brandMarkSrc } from "@/lib/brand";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/auth";

/**
 * Full-screen stop shown instead of the CRM until the user accepts the current
 * Términos y condiciones. There is no separate "Rechazar": not accepting means
 * not entering, and the way out is signing out.
 *
 * Authenticated, so the brand is the CLIENT's (§6.13); the hostname only covers
 * the moment before `/crm/branding` answers.
 */
export function TermsAcceptanceGate(): React.JSX.Element {
  const { data: branding } = useClientBranding();
  const brand = branding ? brandByKey(branding.brand) : brandFromLocation();
  const acceptTerms = useAcceptTerms();
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);

  async function handleSignOut(): Promise<void> {
    await supabase.auth.signOut();
    setSession(null);
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex h-[100dvh] flex-col items-center bg-ink-050 px-4 py-6 overflow-y-auto">
      <div className="mb-5 inline-flex items-center gap-2">
        <img src={brandMarkSrc(brand)} alt="" className="h-[22px] w-[22px] flex-shrink-0" />
        <Wordmark brand={brand} className="text-[17px]" />
      </div>

      <div className="flex w-full max-w-2xl min-h-0 flex-1 flex-col rounded-2xl border border-ink-100 bg-white p-5 sm:p-7">
        <h1 className="font-display text-[22px] font-bold text-ink-900">
          Términos y condiciones
        </h1>
        <p className="mt-1 mb-4 text-[13px] text-ink-500">
          Para usar {brand.name}, lee y acepta los Términos y condiciones.
        </p>

        <div
          className="min-h-[200px] flex-1 overflow-y-auto rounded-xl border border-ink-100 bg-ink-050 px-4 py-3"
          tabIndex={0}
          aria-label="Texto de los Términos y condiciones"
        >
          <TermsContent brand={brand} lastUpdated={legalDateLabel(TERMS_VERSION)} />
        </div>

        <label className="mt-4 flex items-start gap-2 text-[14px] text-ink-700">
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>
            He leído y acepto los{" "}
            <a
              href={brand.termsPath}
              target="_blank"
              rel="noreferrer"
              className="text-moss underline"
            >
              Términos y condiciones
            </a>{" "}
            y el{" "}
            <a
              href={brand.privacyNoticePath}
              target="_blank"
              rel="noreferrer"
              className="text-moss underline"
            >
              Aviso de privacidad
            </a>
            .
          </span>
        </label>

        {acceptTerms.isError && (
          <p role="alert" className="mt-3 text-[13px] text-red-600">
            No pudimos registrar tu aceptación. Intenta de nuevo.
          </p>
        )}

        <button
          type="button"
          disabled={!checked || acceptTerms.isPending}
          onClick={() => acceptTerms.mutate()}
          className="mt-4 w-full py-[14px] bg-lime text-moss rounded-[14px] text-[15px] font-semibold disabled:opacity-60 transition-opacity cursor-pointer disabled:cursor-not-allowed"
        >
          {acceptTerms.isPending ? "Guardando…" : "Aceptar y continuar"}
        </button>

        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="mt-3 text-[13px] text-ink-500 hover:text-ink-900 text-center transition-colors"
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
